import type { QueryClient, UseMutationOptions } from '@tanstack/react-query';
import {
  type AntesDaSaida,
  desfazerSaida,
  marcarEntregue,
  porParaSair,
  SaidaRecusada,
  sairCom,
  voltarParaLoja,
} from '../balcao';
import type { Db } from '../cliente';
import type { ErroApi } from '../erros';
import type { PedidoNaLista } from '../pedidos';
import { chaves } from './chaves';

// Opções das mutações da lista do balcão fora dos hooks: o teste monta a mutação sem React.

export interface VariaveisSairCom {
  pedidoId: string;
  entregadorId: string;
  /** Para a linha otimista já mostrar "Na rua · Carlos" antes da resposta. */
  entregadorNome: string;
}

export interface VariaveisDesfazerSaida {
  pedidoId: string;
  quemSaiu: string;
  antes: AntesDaSaida;
}

export interface VariaveisDoPedido {
  pedidoId: string;
}

export interface VariaveisVoltarParaLoja {
  pedidoId: string;
  /** Quem está com o pedido na tela: a escrita só vale se ainda for ele. */
  entregadorId: string;
}

type Instantaneo = Array<[readonly unknown[], PedidoNaLista[] | undefined]>;

export interface ContextoSairCom {
  anteriores: Instantaneo;
}

/**
 * Dispara sem esperar (padrão de mutacoes-pedidos): devolver a promessa no onSettled prende o botão até
 * todas as consultas relerem. O pedido mexe no dinheiro do dia do entregador (dias do acerto) e no resumo
 * do cliente.
 */
function invalidar(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: chaves.pedidos.todos });
  qc.invalidateQueries({ queryKey: chaves.entregadores.diasTodos });
  qc.invalidateQueries({ queryKey: chaves.clientes.todos });
}

/** Troca a linha em todas as listas do balcão abertas e na tela do pedido. */
function guardarNaFila(qc: QueryClient, pedido: PedidoNaLista): void {
  qc.setQueriesData<PedidoNaLista[]>({ queryKey: chaves.pedidos.filas }, (lista) =>
    lista?.map((p) => (p.id === pedido.id ? pedido : p)),
  );
  qc.setQueryData(chaves.pedidos.um(pedido.id), pedido);
}

/**
 * "Sai com": a linha vai para Na rua na hora; se o banco recusar, volta como estava, ou já como o pedido
 * está agora quando a recusa diz (o app do Rafael saiu antes: a linha mostra o Rafael, sem esperar a releitura).
 */
export function opcoesSairCom(
  db: Db,
  qc: QueryClient,
): UseMutationOptions<PedidoNaLista, ErroApi, VariaveisSairCom, ContextoSairCom> {
  return {
    mutationFn: ({ pedidoId, entregadorId }) => sairCom(db, pedidoId, entregadorId),
    onMutate: async ({ pedidoId, entregadorId, entregadorNome }) => {
      // Uma releitura em voo não pode trazer a linha velha por cima da otimista.
      await qc.cancelQueries({ queryKey: chaves.pedidos.filas });
      const anteriores = qc.getQueriesData<PedidoNaLista[]>({ queryKey: chaves.pedidos.filas });
      const saiuEm = new Date().toISOString();
      qc.setQueriesData<PedidoNaLista[]>({ queryKey: chaves.pedidos.filas }, (lista) =>
        lista?.map((p) =>
          p.id === pedidoId
            ? { ...p, status: 'a_caminho', entregadorId, entregadorNome, saiuEm, motivo: null }
            : p,
        ),
      );
      return { anteriores };
    },
    onError: (erro, _variaveis, contexto) => {
      for (const [chave, lista] of contexto?.anteriores ?? []) qc.setQueryData(chave, lista);
      if (erro instanceof SaidaRecusada && erro.atual) guardarNaFila(qc, erro.atual);
    },
    onSuccess: (pedido) => {
      guardarNaFila(qc, pedido);
    },
    onSettled: () => {
      invalidar(qc);
    },
  };
}

/** Desfazer do "Sai com". A resposta já é a linha de volta na loja (com o nome de quem estava separado). */
export function opcoesDesfazerSaida(
  db: Db,
  qc: QueryClient,
): UseMutationOptions<PedidoNaLista, ErroApi, VariaveisDesfazerSaida> {
  return {
    mutationFn: ({ pedidoId, quemSaiu, antes }) => desfazerSaida(db, pedidoId, quemSaiu, antes),
    onSuccess: (pedido) => {
      guardarNaFila(qc, pedido);
    },
    onSettled: () => {
      invalidar(qc);
    },
  };
}

/** "Entregue" do entregador sem app: a tela só chama depois dos 5 s do Desfazer. */
export function opcoesMarcarEntregue(
  db: Db,
  qc: QueryClient,
): UseMutationOptions<PedidoNaLista, ErroApi, VariaveisDoPedido> {
  return {
    mutationFn: ({ pedidoId }) => marcarEntregue(db, pedidoId),
    onSuccess: (pedido) => {
      guardarNaFila(qc, pedido);
    },
    onSettled: () => {
      invalidar(qc);
    },
  };
}

/** "Voltar para a loja" do pedido a caminho: a tela só chama depois dos 5 s do Desfazer. */
export function opcoesVoltarParaLoja(
  db: Db,
  qc: QueryClient,
): UseMutationOptions<PedidoNaLista, ErroApi, VariaveisVoltarParaLoja> {
  return {
    mutationFn: ({ pedidoId, entregadorId }) => voltarParaLoja(db, pedidoId, entregadorId),
    onSuccess: (pedido) => {
      guardarNaFila(qc, pedido);
    },
    onSettled: () => {
      invalidar(qc);
    },
  };
}

/** Resolver → "Pôr para sair". */
export function opcoesPorParaSair(
  db: Db,
  qc: QueryClient,
): UseMutationOptions<PedidoNaLista, ErroApi, VariaveisDoPedido> {
  return {
    mutationFn: ({ pedidoId }) => porParaSair(db, pedidoId),
    onSuccess: (pedido) => {
      guardarNaFila(qc, pedido);
    },
    onSettled: () => {
      invalidar(qc);
    },
  };
}
